/** @param {KeyboardEvent} event */
function isRestartKey(event) {
  return (
    event.key === 'Enter' ||
    event.code === 'Enter' ||
    event.code === 'NumpadEnter' ||
    event.key === ' ' ||
    event.code === 'Space'
  );
}

/**
 * Full-screen DOM UI so game over is visible even if WebGL/canvas layering misbehaves.
 * Mount from GameScene **before** `start('GameOver')` so the overlay appears the same frame as death.
 * Navigation uses `game.scene` (SceneManager): `stop('GameOver')` then `start(target)` on the next frame
 * so Phaser never runs a half-torn DOM handler inside an `isProcessing` step.
 * @param {Phaser.Game} game
 * @param {object} dataArg
 * @returns {() => void} teardown (safe to call more than once)
 */
export function mountGameOverDom(game, dataArg) {
  const registry = game.registry;

  const passed =
    typeof dataArg === 'object' && dataArg !== null && !Array.isArray(dataArg) ? dataArg : {};

  const storedRun = registry.get('lastRun');
  const stored =
    typeof storedRun === 'object' && storedRun !== null && !Array.isArray(storedRun)
      ? storedRun
      : {};

  const defaults = { survival: 0, growth: 0, pickups: 0 };
  const bdStored =
    stored.breakdown && typeof stored.breakdown === 'object' ? stored.breakdown : {};
  const bdPassed =
    passed.breakdown && typeof passed.breakdown === 'object' ? passed.breakdown : {};
  const breakdown = { ...defaults, ...bdStored, ...bdPassed };

  let scoreRaw =
    typeof passed.score === 'number' && Number.isFinite(passed.score)
      ? passed.score
      : typeof registry.get('finalScore') === 'number' &&
          Number.isFinite(registry.get('finalScore'))
        ? registry.get('finalScore')
        : 0;
  if (!Number.isFinite(scoreRaw)) scoreRaw = 0;

  let elapsedRaw =
    typeof passed.elapsed === 'number' && Number.isFinite(passed.elapsed)
      ? passed.elapsed
      : typeof stored.elapsed === 'number' && Number.isFinite(stored.elapsed)
        ? stored.elapsed
        : 0;
  if (!Number.isFinite(elapsedRaw)) elapsedRaw = 0;

  const floorSafe = (v) => (Number.isFinite(v) ? Math.floor(v) : 0);

  const root = document.createElement('div');
  root.dataset.virutopiaOverlay = 'game-over';
  root.style.cssText = [
    'position:fixed',
    'inset:0',
    'z-index:2147483000',
    'display:flex',
    'flex-direction:column',
    'align-items:center',
    'justify-content:center',
    'gap:10px',
    'padding:24px',
    'box-sizing:border-box',
    'background:rgba(18,4,8,0.92)',
    'color:#ffd5dc',
    'font-family:monospace,ui-monospace,Consolas,menlo,sans-serif',
    'text-align:center',
    'pointer-events:auto',
    'cursor:default',
  ].join(';');

  const title = document.createElement('div');
  title.textContent = 'GAME OVER';
  title.style.cssText = 'font-size:42px;font-weight:bold;color:#ff6677;margin:0;';

  const sub = document.createElement('div');
  sub.textContent = 'You were caught — infection cleared.';
  sub.style.cssText = 'font-size:15px;color:#eeccd8;max-width:520px;';

  const scoreLabel = document.createElement('div');
  scoreLabel.textContent = 'Final score';
  scoreLabel.style.cssText = 'font-size:16px;color:#ddb8c4;margin-top:8px;';

  const scoreVal = document.createElement('div');
  scoreVal.textContent = String(Math.floor(scoreRaw));
  scoreVal.style.cssText = 'font-size:40px;color:#ffeef4;margin:4px 0 12px;';

  const details = document.createElement('pre');
  details.style.cssText =
    'margin:0 0 16px;font-size:13px;line-height:1.55;color:#ccaabb;text-align:left;';
  details.textContent = [
    'Score breakdown (approx.)',
    `Survival drift    ${floorSafe(breakdown.survival)}`,
    `Growth streaming  ${floorSafe(breakdown.growth)}`,
    `Pickups           ${floorSafe(breakdown.pickups)}`,
    '',
    `Time survived ${elapsedRaw.toFixed(1)} s`,
  ].join('\n');

  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;justify-content:center;margin-top:8px;';

  const mkBtn = (label, primary) => {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.style.cssText = [
      'font-family:inherit',
      'font-size:15px',
      'cursor:pointer',
      'padding:10px 18px',
      'border-radius:6px',
      'border:2px solid',
      primary
        ? 'background:#7a2233;border-color:#ff8899;color:#fff'
        : 'background:transparent;border-color:#886677;color:#ddbbbb',
    ].join(';');
    return b;
  };

  const btnRestart = mkBtn('Play again', true);
  const btnMenu = mkBtn('Main menu', false);

  const hint = document.createElement('div');
  hint.textContent = 'Enter / Space — play again  •  Esc — menu';
  hint.style.cssText = 'font-size:13px;color:#aa8894;margin-top:14px;';

  btnRow.appendChild(btnRestart);
  btnRow.appendChild(btnMenu);

  root.appendChild(title);
  root.appendChild(sub);
  root.appendChild(scoreLabel);
  root.appendChild(scoreVal);
  root.appendChild(details);
  root.appendChild(btnRow);
  root.appendChild(hint);

  let tornDown = false;

  const teardown = () => {
    if (tornDown) return;
    tornDown = true;
    window.removeEventListener('keydown', onWindowKeyDown);
    registry.remove('gameOverOverlayTeardown');
    root.remove();
  };

  const scheduleSceneSwitch = (targetKey) => {
    const mgr = game.scene;
    requestAnimationFrame(() => {
      mgr.stop('GameOver');
      mgr.start(targetKey);
    });
  };

  const goRestart = () => {
    teardown();
    scheduleSceneSwitch('Game');
  };

  const goMenu = () => {
    teardown();
    scheduleSceneSwitch('MainMenu');
  };

  const onWindowKeyDown = (event) => {
    if (isRestartKey(event)) {
      event.preventDefault();
      goRestart();
    } else if (event.key === 'Escape' || event.code === 'Escape') {
      event.preventDefault();
      goMenu();
    }
  };

  window.addEventListener('keydown', onWindowKeyDown);
  btnRestart.addEventListener('click', goRestart);
  btnMenu.addEventListener('click', goMenu);

  document.body.appendChild(root);

  window.setTimeout(() => {
    game.canvas?.focus?.({ preventScroll: true });
  }, 0);

  return teardown;
}
